import styled, { keyframes } from "styled-components";
import { ProcessingShimmerText } from "../ProcessingMessage";

export const SidebarWrapper = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.background.primary};
  width: 240px;
  padding: 0.75rem 0.5rem 0.75rem 0.75rem;
  z-index: 1000;
  align-items: stretch;
  justify-content: flex-start;
  overflow: hidden;
  border-right: 1px solid ${(props) => props.theme.colors.border};
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    display: none;
  }
`;

// The logo (above, in SidebarWrapper) and the user section (below, a sibling
// of this in index.tsx) stay pinned — this column itself never scrolls.
// Only the "Secciones del informe" list, deep inside ReportSectionNavDesktop,
// scrolls internally (see NavWrapper/SectionList in ReportSectionNav.tsx) —
// min-height: 0 here is what lets that inner flex child actually shrink
// instead of pushing this whole column (and the pinned logo/user) around.
export const SidebarContent = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  // The whole gap under the logo. Only ever seen from md up, where the sidebar
  // stops being a burger, so it needs no breakpoint of its own.
  margin-top: 1.5rem;
  gap: 0.15rem;
`;

// Subtle separator between the main app nav (Copilot/Due Diligence/Contracts)
// and page-specific content appended below it (e.g. the DD report section
// nav) — barely visible on purpose, just enough to mark the boundary.
export const SidebarDivider = styled.hr`
  width: 100%;
  height: 1px;
  border: none;
  background: rgba(255, 255, 255, 0.08);
  margin: 0.75rem 0;
`;

export const ActionButton = styled.button<{ $isCollapsed?: boolean }>`
  width: ${(props) => (props.$isCollapsed ? "100%" : "60%")};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem ${(props) => props.theme.spacing.medium};
  color: ${(props) => props.theme.colors.font.secondary};
  font-size: ${(props) => props.theme.fontSizes.medium};
  font-weight: 400;
  outline: 0;
  border: 0;
  border-radius: ${(props) => (props.$isCollapsed ? "8px" : "1.5rem")};
  cursor: pointer;
  transition: background 0.1s ease-in-out, border 0.1s ease-in-out;
  background: ${(props) => props.theme.colors.accent1};
  margin-top: ${(props) => props.theme.spacing.medium};
  margin-bottom: ${(props) => props.theme.spacing.large};
  &:hover {
    background: ${(props) => props.theme.colors.accent2};
  }
  > svg {
    margin-left: ${(props) => (props.$isCollapsed ? "0" : props.theme.spacing.small)};
    color: ${(props) => props.theme.colors.font.secondary};
  }
`;

export const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  // No bottom spacing of its own: the gap down to the nav is SidebarContent's
  // margin-top alone, so there is one number to turn rather than three that
  // quietly add up.
  padding: 0 ${(props) => props.theme.spacing.medium};
  > img {
    width: 60%;
    max-width: 120px;
    height: auto;
  }
`;

export const SidebarBody = styled.div<{ $isCollapsed?: boolean }>`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$isCollapsed ? "center" : "center")};
  justify-content: flex-start;
  position: relative;
`;

export const SidebarItem = styled.div<{ $isCollapsed?: boolean }>`
  display: flex;
  width: ${(props) => (props.$isCollapsed ? "100%" : "60%")};
  align-items: center;
  justify-content: ${(props) => (props.$isCollapsed ? "center" : "flex-start")};
  padding: ${(props) => (props.$isCollapsed ? "0.75rem" : props.theme.spacing.xsmall)};
  margin-bottom: ${(props) => props.theme.spacing.small};
  border-radius: ${(props) => (props.$isCollapsed ? "8px" : "1.5rem")};
  cursor: pointer;
  transition: 0.1s;
  color: ${(props) => props.theme.colors.font.secondary};
  font-size: ${(props) => props.theme.fontSizes.large};
  font-weight: 300;
  gap: ${(props) => props.theme.spacing.medium};
  padding-left: ${(props) => (props.$isCollapsed ? "0.75rem" : "1.2rem")};
  &.non-active {
    &:hover {
      background-color: ${(props) => props.theme.colors.sidebar.selected};
      color: ${(props) => props.theme.colors.accent1};
    }
  }
  &.active {
    background-color: ${(props) =>
      props.theme.colors.sidebar.selected} !important;
    color: ${(props) => props.theme.colors.accent1};
  }
  > svg {
    flex: 0 0 auto;
    height: ${(props) => props.theme.fontSizes.large};
    width: ${(props) => props.theme.fontSizes.large};
  }
`;

export const TrialBanner = styled.div<{ $type: 'info' | 'warning' }>`
  margin: 1rem 0;
  padding: 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${(props) => {
    if (props.$type === 'warning') {
      return '#fef3c7';
    }
    return '#dbeafe';
  }};
  border: 1px solid ${(props) => {
    if (props.$type === 'warning') {
      return '#fbbf24';
    }
    return '#60a5fa';
  }};
  color: ${(props) => {
    if (props.$type === 'warning') {
      return '#92400e';
    }
    return '#1e40af';
  }};
  text-align: center;
  font-weight: 500;

  &:hover {
    background-color: ${(props) => {
      if (props.$type === 'warning') {
        return '#fde68a';
      }
      return '#bfdbfe';
    }};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

export const UserSection = styled.div`
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid ${(props) => props.theme.colors.border};
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const UserAvatar = styled.div<{ $bgColor?: string; $textColor?: string }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(props) => props.$bgColor || "#4a5568"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$textColor || "white"};
  font-weight: 600;
  font-size: 0.75rem;
  flex-shrink: 0;
`;

export const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  overflow: hidden;

  > div {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const UserMenu = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${(props) => props.$top}px;
  left: ${(props) => props.$left}px;
  width: 240px;
  transform: translateY(calc(-100% - 0.5rem));
  background: ${(props) => props.theme.colors.primary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 3000;
`;

export const UserMenuHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 1rem;
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  background-color: ${(props) => props.theme.colors.secondary};
`;

export const UserMenuName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.font.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const UserMenuEmail = styled.div`
  font-size: 0.75rem;
  color: ${(props) => props.theme.colors.font.grey2};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const UserMenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  color: ${(props) => props.theme.colors.font.primary};
  font-size: 0.875rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${(props) => props.theme.colors.secondary};
  }

  svg {
    flex-shrink: 0;
  }
`;

export const MainNavList = styled.div<{ $isRail: boolean }>`
  position: relative;
  display: flex;
  flex-direction: ${(props) => (props.$isRail ? "row" : "column")};
  align-items: center;
  gap: ${(props) => (props.$isRail ? "0.35rem" : "0.4rem")};
  padding: ${(props) => (props.$isRail ? "0 0.25rem" : "0")};
`;

export const MainNavButton = styled.button<{ $isActive: boolean; $isRail: boolean }>`
  font: inherit;
  color: inherit;
  text-align: left;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  border: none;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  transition: background-color 0.2s;

  ${(props) =>
    props.$isRail
      ? `
    flex: 0 0 auto;
    width: 2.25rem;
    height: 2.25rem;
    padding: 0;
    justify-content: center;
  `
      : `
    align-self: stretch;
    width: auto;
    padding: 0.6rem 0.75rem;
    justify-content: flex-start;
  `}

  background-color: ${(props) => (props.$isActive ? "rgba(209, 250, 229, 0.1)" : "transparent")};

  ${(props) =>
    props.$isActive &&
    !props.$isRail &&
    `
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background-color: #D1FAE5;
      border-radius: 0 2px 2px 0;
    }
  `}

  &:hover {
    background-color: ${(props) =>
      props.$isActive ? "rgba(209, 250, 229, 0.1)" : "rgba(255, 255, 255, 0.05)"};
  }
`;

export const MainNavIcon = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${(props) => (props.$isActive ? "#D1FAE5" : "#ffffff")};
  transition: color 0.2s;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

// Gone rather than merely invisible in the rail: the label does not wrap and
// does not shrink, so leaving it in the flow would overflow a 2.25rem button
// and, with the icon centred in that overflow, push the icon out of the box
// and under the button's own overflow clip.
export const MainNavLabel = styled.div<{ $isActive: boolean; $isRail: boolean }>`
  display: ${(props) => (props.$isRail ? "none" : "block")};
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 0.8rem;
  font-weight: ${(props) => (props.$isActive ? 600 : 400)};
  color: ${(props) => (props.$isActive ? "#D1FAE5" : "#ffffff")};
  transition: color 0.2s;
`;

export const MainNavBadgeSlot = styled.div<{ $isRail: boolean }>`
  display: ${(props) => (props.$isRail ? "none" : "flex")};
  flex-shrink: 0;
  align-items: center;
`;

const mainNavFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-6px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// The way out of the report, sitting ahead of the destinations it is not one
// of — hence the separator between them. It has no counterpart in the
// labelled shape, so it fades in on its own rather than morphing.
export const MainNavBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background-color: transparent;
  color: rgba(255, 255, 255, 0.75);
  transition: background-color 0.2s, color 0.2s;
  animation: ${mainNavFadeIn} 300ms ease-out both;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    color: #ffffff;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

export const MainNavSeparator = styled.div`
  flex: 0 0 auto;
  width: 1px;
  height: 1.25rem;
  margin: 0 0.2rem;
  background-color: rgba(255, 255, 255, 0.12);
  animation: ${mainNavFadeIn} 300ms ease-out both;
`;

// The tooltip mounts the moment the pointer lands, but it does not show up
// that fast: it holds still for a beat and then eases in, so sweeping across
// the rail on the way somewhere else never sets off a string of them.
const mainNavTooltipFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const MainNavTooltip = styled.div`
  position: absolute;
  top: calc(100% + 0.4rem);
  left: 0;
  right: 0;
  z-index: 1002;
  padding: 0.5rem 0.6rem;
  border-radius: 8px;
  background-color: rgba(17, 24, 39, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);
  pointer-events: none;

  opacity: 0;
  animation: ${mainNavTooltipFadeIn} 260ms ease-out 220ms forwards;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 1ms;
    animation-delay: 0ms;
  }
`;

export const MainNavTooltipTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #ffffff;
`;

export const MainNavTooltipDescription = styled.div`
  margin-top: 0.15rem;
  font-size: 0.7rem;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.7);
`;

export const SubSidebarWrapper = styled.div`
  position: fixed;
  left: 240px;
  top: 0;
  bottom: 0;
  width: 280px;
  background-color: #ffffff;
  border-right: 1px solid ${(props) => props.theme.colors.border};
  display: flex;
  flex-direction: column;
  z-index: 1001;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
  overflow-y: auto;
  overflow-x: hidden;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    display: none;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

export const SubSidebarHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const SubSidebarSearch = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  background-color: ${(props) => props.theme.colors.secondary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  min-height: 40px;

  svg {
    color: #666666;
    flex-shrink: 0;
  }

  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.875rem;
    color: #1a1a1a;
    font-weight: 400;
    line-height: 1.5;
    padding: 0;

    &::placeholder {
      color: #999999;
    }
  }
`;

export const SubSidebarChatList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

export const SubSidebarChatItem = styled.div<{ $isActive?: boolean }>`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  margin-bottom: 0.25rem;
  background-color: ${(props) => (props.$isActive ? "rgba(0, 0, 0, 0.05)" : "transparent")};
  line-height: 1.5;
  color: ${(props) => (props.$isActive ? "#1a1a1a" : "#666666")};

  &:hover {
    background-color: ${(props) =>
      props.$isActive
        ? "rgba(0, 0, 0, 0.05)"
        : "rgba(0, 0, 0, 0.03)"
    };
    color: ${(props) => (props.$isActive ? "#1a1a1a" : "#1a1a1a")};
  }
`;

export const SubSidebarDateSeparator = styled.div`
  padding: 1rem 0.75rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #999999;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const SubSidebarNewChatButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background-color: #000000;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin: 0;

  &:hover {
    background-color: #1a1a1a;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    flex-shrink: 0;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
`;

export const ModalContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 900px;
  background-color: #ffffff;
  border-radius: 1rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #F3F4F6;
  flex-shrink: 0;
`;

export const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.025em;
`;

export const ModalCloseButton = styled.button`
  padding: 0.5rem;
  border-radius: 9999px;
  border: none;
  background-color: transparent;
  color: #9CA3AF;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: #F3F4F6;
    color: #4B5563;
  }

  &:focus {
    outline: none;
  }
`;

export const ModalStaffBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  border: 1px solid #CBD5E1;
  background-color: #F1F5F9;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  margin: 0 0.5rem 0 1rem;
`;

/** The same sweep as the DD tasks in progress, so the staff badge catches the eye. */
export const ModalStaffBadgeText = styled(ProcessingShimmerText)`
  font-size: inherit;
  font-style: normal;
  font-weight: inherit;
`;

export const ModalBody = styled.div`
  overflow-y: auto;
  padding: 1.5rem 2rem;
  flex: 1;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #CBD5E1;
    border-radius: 20px;
  }
`;

export const ModalHeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ModalHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const ModalHeaderTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ModalSubtitle = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0.25rem 0 0 0;
`;

export const InviteSection = styled.div`
  background-color: #f9fafb;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border: 1px dashed #e5e7eb;
`;

export const InviteSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

export const InviteSectionLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
`;

export const MemberLimitIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const MemberLimitText = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
`;

export const MemberLimitBar = styled.div`
  width: 60px;
  height: 4px;
  background-color: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
`;

export const MemberLimitProgress = styled.div<{ $percentage: number; $hasReachedLimit: boolean }>`
  width: ${(props) => Math.min(props.$percentage, 100)}%;
  height: 100%;
  background-color: ${(props) =>
    props.$hasReachedLimit ? "#ef4444" : props.$percentage > 80 ? "#f59e0b" : "#10b981"};
  transition: all 0.3s ease;
`;

export const LimitWarning = styled.div`
  background-color: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

export const LimitWarningContent = styled.div`
  flex: 1;
`;

export const LimitWarningTitle = styled.p`
  font-size: 0.875rem;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 0.25rem 0;
`;

export const LimitWarningText = styled.p`
  font-size: 0.875rem;
  color: #92400e;
  margin: 0 0 0.75rem 0;
`;

export const UpgradeButton = styled.button`
  background-color: #000000;
  color: #ffffff;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #1a1a1a;
  }
`;

export const InviteFormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const InputWrapper = styled.div`
  position: relative;
`;

export const InputIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
`;

export const StyledInput = styled.input<{ $disabled?: boolean }>`
  width: 100%;
  padding: 0.625rem 0.75rem 0.625rem 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  outline: none;
  transition: border-color 0.2s;
  background-color: ${(props) => (props.$disabled ? "#f3f4f6" : "#ffffff")};
  color: ${(props) => (props.$disabled ? "#9ca3af" : "#111827")};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "text")};

  &:focus {
    border-color: ${(props) => (props.$disabled ? "#d1d5db" : "#3b82f6")};
  }
`;

export const InviteFormActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const StyledSelect = styled.select<{ $disabled?: boolean }>`
  flex: 1;
  padding: 0.625rem 2rem 0.625rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  outline: none;
  background-color: ${(props) => (props.$disabled ? "#f3f4f6" : "#ffffff")};
  color: ${(props) => (props.$disabled ? "#9ca3af" : "#111827")};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
`;

export const InviteButton = styled.button<{ $disabled?: boolean; $isLoading?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background-color: ${(props) => (props.$disabled ? "#9ca3af" : "#021526")};
  color: #ffffff;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  white-space: nowrap;
  transition: background-color 0.2s;
  opacity: ${(props) => (props.$isLoading ? 0.7 : 1)};

  &:hover {
    background-color: ${(props) => (props.$disabled ? "#9ca3af" : "#032940")};
  }
`;

export const MembersListSection = styled.div``;

export const MembersListHeader = styled.h4`
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
`;

export const MembersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const MemberCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  transition: all 0.2s;
`;

export const MemberAvatar = styled.div<{ $bgColor: string; $textColor: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${(props) => props.$bgColor};
  color: ${(props) => props.$textColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 700;
  flex-shrink: 0;
`;

export const MemberInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const MemberNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const MemberName = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MemberBadge = styled.span<{ $type: "current" | "pending" }>`
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  display: ${(props) => (props.$type === "pending" ? "inline-flex" : "inline-block")};
  align-items: ${(props) => (props.$type === "pending" ? "center" : "initial")};
  gap: ${(props) => (props.$type === "pending" ? "0.25rem" : "0")};
  color: ${(props) => (props.$type === "current" ? "#059669" : "#d97706")};
  background-color: ${(props) => (props.$type === "current" ? "#d1fae5" : "#fef3c7")};
`;

export const MemberEmail = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MemberActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const MemberRoleLabel = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
`;

export const MemberRoleSelect = styled.select<{ $disabled?: boolean }>`
  padding: 0.25rem 1.5rem 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  outline: none;
  background-color: #ffffff;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
`;

export const DeleteButton = styled.button`
  padding: 0.5rem;
  border: none;
  background-color: transparent;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    color: #ef4444;
    background-color: #fee2e2;
  }
`;

export const ConfirmModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
`;

export const CancelButton = styled.button`
  padding: 0.625rem 1rem;
  border: 1px solid #d1d5db;
  background-color: #ffffff;
  color: #374151;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f9fafb;
  }
`;

export const ConfirmButton = styled.button`
  padding: 0.625rem 1rem;
  border: none;
  background-color: #dc2626;
  color: #ffffff;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #b91c1c;
  }
`;

export const ConfirmModalText = styled.p`
  font-size: 0.875rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.6;
`;
